import { useContext } from 'react';
import { getIdToken, setWindowURLParams } from 'utils/utils';
import { AuthenticationContext } from 'contexts/authentication.context';
import usePageInfo from 'hooks/pageInfo.hook';
import { AssetListPageContext } from 'contexts/assetListPage.context';
import { EstimateType } from 'types/Estimate';
import { alertErrorMessage } from 'utils/alerts';
import { outdoorLocationString } from 'components/Header/components/AssetSearch/AssetSearchAsyncSelect';
import AssetsApi from 'api/assets/assets.api';

export default function useAssetPageListHandlers() {
  const authContext = useContext(AuthenticationContext).authState;
  const token = getIdToken(authContext);
  const { setHeaderAssetCount } = usePageInfo();

  const {
    searchTerm,
    setSearchTerm,
    searchCategory,
    setIsSearching,
    sortColumnField,
    sortDirection,
    setSortColumnField,
    setSortDirection,
    assetTableData,
    setAssetTableData,
    setIsSorting,
    setIsLoading,
    nextToken,
    setNextToken,
    setNetworkError,
    isSearching,
    cachedAssetTableDataRef,
    cachedAssetCountRef,
    cachedNextTokenRef,
    setFetchingNextPage,
    setPaginationIndex,
    paginationIndex,
    cachedPaginationIndexRef,
    setDownloadAssetsButtonDisabled,
  } = useContext(AssetListPageContext);

  function getSearchAPICall() {
    const assetsApi = new AssetsApi(token);

    const hasEnterSearch = searchCategory === ''; // enter search will have no category assigned.

    const apiCall = hasEnterSearch
      ? assetsApi.getAllOptionsSearchEstimates
      : assetsApi.getSingleOptionSearchEstimates;

    return apiCall;
  }

  function getSearchAPICallArgs(nextToken: string) {
    const hasEnterSearch = searchCategory === ''; // enter search will have no category assigned.

    const args = hasEnterSearch
      ? [searchTerm, nextToken, sortColumnField, sortDirection]
      : [searchTerm, searchCategory, nextToken, sortColumnField, sortDirection];
    return args;
  }

  const callFetchEstimatesSearch = async (): Promise<any> => {
    const assetsApi = new AssetsApi(token);

    const hasEnterSearch = searchCategory === ''; // enter search will have no category assigned.

    const apiCall = hasEnterSearch
      ? assetsApi.getAllOptionsSearchEstimates
      : assetsApi.getSingleOptionSearchEstimates;

    const args = hasEnterSearch
      ? [searchTerm, '', sortColumnField, sortDirection]
      : [searchTerm, searchCategory, '', sortColumnField, sortDirection];

    apiCall(...args)
      .then((res) => {
        setIsSearching(true);
        setHeaderAssetCount(res.total);
        setAssetTableData(res.results);
        setNextToken(res.next);
        setIsLoading(false);
        setIsSorting(false);
        setPaginationIndex(0); // reset pagination to 0.
      })
      .catch((err) => {
        alertErrorMessage('Could not fetch Assets');
        setIsLoading(false);
        console.error(err);
      });
  };

  const callFetchEstimates = async (): Promise<any> => {
    const assetsApi = new AssetsApi(token);

    setIsLoading(true);
    setDownloadAssetsButtonDisabled(true);
    assetsApi
      .getAssets(nextToken, sortColumnField, sortDirection)
      .then((res: any) => {
        setHeaderAssetCount(res.total);
        setAssetTableData(res.results);
        setNextToken(res.next);
        setDownloadAssetsButtonDisabled(res.total === 0); // enable if asset count is 0.

        setIsLoading(false);
        setIsSorting(false);

        if (!isSearching) {
          // do not cache refs if user is searching, so we can revert, incase they clear search field.
          cachedAssetCountRef.current = res.total;
          cachedAssetTableDataRef.current = res.results;
          cachedNextTokenRef.current = res.next;
          cachedPaginationIndexRef.current = paginationIndex;
        }
      })
      .catch((err) => {
        alertErrorMessage('Could not fetch Assets');
        setIsLoading(false);
        setIsSearching(false);
        setNetworkError(err);
      });
  };

  const callFetchEstimatesLoop = async (): Promise<any> => {
    // call estimates API in loop to meet the pagination index count.
    // use the next token from each calls res for the next iteration.
    // combine results and set total once pagination index is met.
    setIsLoading(true);
    let combinedResults: EstimateType[] = [];
    let assetTotal = 0;
    let internalNextToken = '';

    for (let index = 0; index <= paginationIndex; index++) {
      await iterationCall(index);
    }

    async function iterationCall(index: number) {
      const assetsApi = new AssetsApi(token);
      const apiCall = isSearching ? getSearchAPICall() : assetsApi.getAssets;
      const args = isSearching
        ? getSearchAPICallArgs(internalNextToken)
        : [internalNextToken, sortColumnField, sortDirection];

      await apiCall(...args)
        .then((res: any) => {
          assetTotal = res.total;
          combinedResults = [...combinedResults, ...res.results];
          internalNextToken = res.next;

          setIsLoading(false);
          setIsSorting(false);

          if (index === paginationIndex) {
            setHeaderAssetCount(assetTotal);
            setAssetTableData(combinedResults);
            setNextToken(internalNextToken);
            setIsLoading(false);
            setIsSorting(false);
          }
        })
        .catch((err) => {
          alertErrorMessage('Could not fetch Assets');
          console.log(err);
          setIsLoading(false);
          setIsSearching(false);
          setNetworkError(err);
        });
    }
  };

  const handleSort = async (sortField: string, direction: string) => {
    const assetsApi = new AssetsApi(token);

    setIsSorting(true);
    setSortColumnField(sortField);
    setSortDirection(direction);

    assetsApi
      .getAssets('', sortField, direction, searchTerm, searchCategory) // empty null token, to reset pagination on sort.
      .then((res: any) => {
        setHeaderAssetCount(res.total);
        setAssetTableData(res.results);
        setNextToken(res.next);
        setIsLoading(false);
        setIsSorting(false);
        setFetchingNextPage(false);
        setPaginationIndex(0);

        if (!isSearching) {
          // do not cache refs if user is searching, so we can revert, incase they clear search field.
          cachedAssetCountRef.current = res.total;
          cachedAssetTableDataRef.current = res.results;
          cachedNextTokenRef.current = res.next;
          cachedPaginationIndexRef.current = paginationIndex;
        }
      })
      .catch((err) => {
        alertErrorMessage('Could not fetch Assets');
        setIsLoading(false);
        setIsSorting(false);

        console.error(err);
        setFetchingNextPage(false);
        setNetworkError(err);
      });
  };

  const handlePagination = () => {
    const assetsApi = new AssetsApi(token);

    setFetchingNextPage(true);

    assetsApi
      .getAssets(nextToken, sortColumnField, sortDirection, searchTerm, searchCategory)
      .then((res: any) => {
        const combinedResults: EstimateType[] = [...assetTableData, ...res.results];

        setAssetTableData(combinedResults);
        setNextToken(res.next);
        setIsLoading(false);
        setIsSorting(false);
        setFetchingNextPage(false);
        setPaginationIndex(paginationIndex + 1);

        if (!isSearching) {
          // do not cache refs if user is searching, so we can revert, incase they clear search field.
          cachedAssetTableDataRef.current = combinedResults;
          cachedNextTokenRef.current = res.next;
        }
      })
      .catch((err) => {
        alertErrorMessage('Could not fetch Assets');
        setIsLoading(false);
        setNetworkError(err);
        console.error(err);
        setFetchingNextPage(false);
      });
  };

  function handleEnterSearch(searchTerm: string) {
    const assetsApi = new AssetsApi(token);
    const termTrimmed = searchTerm.trim();

    setSearchTerm(termTrimmed);
    setIsLoading(true);

    assetsApi
      .getAllOptionsSearchEstimates(termTrimmed, '', sortColumnField, sortDirection)
      .then((res) => {
        setIsSearching(true);
        setHeaderAssetCount(res.total);
        setAssetTableData(res.results);
        setNextToken(res.next);
        setIsLoading(false);
        setIsSorting(false);
        setPaginationIndex(0);

        setWindowURLParams(['term', termTrimmed]);
      })
      .catch((err) => {
        alertErrorMessage('Could not fetch Assets');
        setIsLoading(false);
        console.error(err);
      });
  }

  function handleClickOptionSearch(searchTerm: string, searchCategory: string) {
    const assetsApi = new AssetsApi(token);
    const termTrimmed = searchTerm.trim();

    setIsLoading(true);
    setSearchTerm(searchCategory === 'is_outdoors' ? outdoorLocationString : termTrimmed);
    assetsApi
      .getSingleOptionSearchEstimates(
        termTrimmed,
        searchCategory,
        '',
        sortColumnField,
        sortDirection,
      )
      .then((res) => {
        setIsSearching(true);
        setHeaderAssetCount(res.total);
        setAssetTableData(res.results);
        setNextToken(res.next);
        setIsLoading(false);
        setIsSorting(false);
        setPaginationIndex(0);

        setWindowURLParams(['term', termTrimmed], ['category', searchCategory]);
      })
      .catch((err) => {
        alertErrorMessage('Could not fetch Assets');
        setIsLoading(false);
        console.error(err);
      });
  }

  return {
    callFetchEstimatesSearch,
    callFetchEstimates,
    callFetchEstimatesLoop,
    handleSort,
    handlePagination,
    handleEnterSearch,
    handleClickOptionSearch,
  };
}
